import { Box, Grid, IconButton } from "@mui/material";
import { Form, Formik } from "formik";
import { FC, useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import CTypography from "src/components/CTypography";
import FileDropzone from "src/components/dropzone";
import FormikControl from "src/components/formik/FormikControl";
import { IComplaintForm, IComplaintFormProps } from "src/pages/customer/types";
import {
  selectComplaintCategories,
  selectSelectedComplaint,
} from "src/slices/complaints/selectors";
import {
  fetchComplaintCategories,
  fetchComplaintByComplaintId,
  uploadComplaintAttachments,
  fetchComplaints,
} from "src/slices/complaints/thunks";
import {
  IComplaint,
  IComplaintData,
  IComplaintPayload,
} from "src/slices/complaints/types";
import { selectCustomerIds } from "src/slices/customers/selectors";
import { fetchCustomerIds } from "src/slices/customers/thunks";
import { selectOrderIds } from "src/slices/orders/selectors";
import { fetchOrderIdsByCustomerId } from "src/slices/orders/thunks";
import { setDialog } from "src/slices/uiSettingsSlice";
import { AppDispatch } from "src/store";
import CloseIcon from "@mui/icons-material/Close";
import CModalConfirmation from "src/components/CModalConfirmation";
import { selectLocations } from "src/slices/configuration/selectors";

export const ComplaintForm: FC<IComplaintFormProps> = ({
  dialogName,
  customerId,
  complaintId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const formikRef = useRef<any>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [attachmentToRemove, setAttachmentToRemove] = useState<string[]>([]);
  const [selectedAttachment, setSelectedAttachment] =
    useState<IComplaintData>();

  const orderIds = useSelector(selectOrderIds);
  const complaintCategories = useSelector(selectComplaintCategories);
  const selectedComplaint = useSelector(selectSelectedComplaint);
  const selectedCustomerIds = useSelector(selectCustomerIds);
  const locations = useSelector(selectLocations);

  // ✅ Memoized "Others" logic (no unnecessary re-renders)
  const complaintCategoriesWithOthers = useMemo(() => {
    const hasOthers = complaintCategories.some(
      (cat: any) => cat.name === "Others" || cat.category === "Others"
    );
    return hasOthers
      ? complaintCategories
      : [...complaintCategories, { id: "Others", category: "Others" }];
  }, [complaintCategories]);

  // ✅ Fetch initial data (run once)
  useEffect(() => {
    dispatch(fetchCustomerIds());
    dispatch(fetchComplaintCategories());
    if (complaintId) dispatch(fetchComplaintByComplaintId(complaintId));
    if (customerId) dispatch(fetchOrderIdsByCustomerId(customerId));
  }, [dispatch, complaintId, customerId]);

  // ✅ Prefill form when complaint data available
  useEffect(() => {
    if (!complaintId || !selectedComplaint || locations.length === 0) return;

    const selectedOrder = orderIds?.find(
      (order) => order.id === selectedComplaint.orderId
    );

    const valuesToSet = {
      order: selectedComplaint.orderId || "",
      branchId: selectedOrder?.location || "",
      category: selectedComplaint.categoryId || "",
      detail: selectedComplaint.description || "",
      isResolved: selectedComplaint.isResolved || false,
      resolvedDate: selectedComplaint.resolvedDate || null,
    };

    Object.entries(valuesToSet).forEach(([key, value]) => {
      formikRef.current?.setFieldValue(key, value);
    });
  }, [complaintId, selectedComplaint, orderIds, locations]);

  // ✅ Prefill customer name
  useEffect(() => {
    if (!selectedCustomerIds?.length || !customerId) return;

    const selectedCustomer = selectedCustomerIds.find(
      (customer) => customer.customerId === customerId
    );

    if (selectedCustomer?.customerName) {
      formikRef.current?.setFieldValue(
        "customer",
        selectedCustomer.customerName
      );
    }
  }, [selectedCustomerIds, customerId]);

  // ✅ Initial Form Values
  const formInitialValue: IComplaintForm = {
    order: "",
    customer: "",
    category: "",
    detail: "",
    isResolved: false,
    branchId: "",
    resolvedDate: null,
  };

  // ✅ Submit Handler
  const onClickSubmit = async (values: IComplaintForm) => {
    const selectedCustomer = selectedCustomerIds.find(
      (customer) => customer.customerName === values.customer
    );
    const customerId = selectedCustomer?.customerId || "";

    const categoryId =
      values.category === "Others" ? "Others" : Number(values.category);

    // ✅ Clean complaint data
    const attachments =
      complaintId && selectedComplaint
        ? selectedComplaint.attachments
            .filter((a) => !attachmentToRemove.includes(a.fileUrl))
            .map(({ fileName, fileType, fileUrl }) => ({
              fileName,
              fileType,
              fileUrl,
            }))
        : [];

    const complaintData: IComplaint = {
      complaintId: complaintId ?? null,
      orderId: values.order,
      customerId,
      description: values.detail,
      categoryId,
      isResolved: complaintId ? values.isResolved : false,
      resolvedDate: complaintId ? values.resolvedDate : null,
      branchId: values.branchId,
      attachments,
    };

    const { branchId, ...complaintDataWithoutBranchId } = complaintData;

    const complaintPayload: IComplaintPayload = {
      files,
      // complaintData,
      complaintData: complaintDataWithoutBranchId,
    };

    dispatch(uploadComplaintAttachments(complaintPayload)).then((response) => {
      if (response.payload && !response.payload.error) {
        dispatch(fetchComplaints());
        dispatch(setDialog({ open: false, dialogName }));
      }
    });
  };

  return (
    <Box sx={{ width: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Formik
            initialValues={formInitialValue}
            innerRef={formikRef}
            onSubmit={onClickSubmit}
          >
            {({ touched, errors, setFieldValue, getFieldProps }) => (
              <Form id={dialogName}>
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Complaint Information
                    </CTypography>
                  </Grid>

                  {/* Customer */}
                  <Grid item xs={12} sm={6}>
                    <FormikControl
                      control="VirtualizedSearchableSelect"
                      required
                      name="customer"
                      label="Customer"
                      options={selectedCustomerIds.map(
                        (c) => c.customerName || "--"
                      )}
                      handleChange={(value) => {
                        setFieldValue("order", "");
                        setFieldValue("branchId", "");
                        setFieldValue("customer", value);

                        const selectedCustomer = selectedCustomerIds.find(
                          (c) => c.customerName === value
                        );
                        if (selectedCustomer) {
                          setFieldValue(
                            "branchId",
                            selectedCustomer.customerLocation || ""
                          );

                          dispatch(
                            fetchOrderIdsByCustomerId(
                              selectedCustomer.customerId
                            )
                          );
                        }
                      }}
                      error={Boolean(touched.customer && errors.customer)}
                      helperText={touched.customer && errors.customer}
                      {...getFieldProps("customer")}
                    />
                  </Grid>

                  {/* Order */}
                  <Grid item xs={12} sm={6}>
                    <FormikControl
                      control="SelectField"
                      required
                      name="order"
                      label="Order"
                      disabled={!orderIds?.length}
                      options={
                        orderIds?.map((o) => ({
                          value: o.id,
                          label: `LX-${o.id}`,
                        })) || []
                      }
                      handleChange={(e) => {
                        setFieldValue("order", e.target.value);
                        // // !Removed branch auto-update here
                        // const selectedOrder = orderIds.find(
                        //   (order) => order.id === e.target.value
                        // );
                        // setFieldValue(
                        //   "branchId",
                        //   selectedOrder?.location || ""
                        // );
                      }}
                      error={Boolean(touched.order && errors.order)}
                      helperText={
                        touched.order && errors.order
                          ? errors.order
                          : !orderIds?.length
                          ? "Please select a customer first"
                          : ""
                      }
                      {...getFieldProps("order")}
                    />
                  </Grid>

                  {/* Category */}
                  <Grid item xs={12} sm={6}>
                    <FormikControl
                      control="SelectField"
                      required
                      name="category"
                      label="Category"
                      options={complaintCategoriesWithOthers.map((c) => ({
                        value: c.id,
                        label: c.category || c.name,
                      }))}
                      error={Boolean(touched.category && errors.category)}
                      helperText={touched.category && errors.category}
                      {...getFieldProps("category")}
                    />
                  </Grid>

                  {/* Branch */}
                  <Grid item xs={12} sm={6}>
                    <FormikControl
                      control="SelectField"
                      name="branchId"
                      label="Branch Name"
                      disabled
                      options={selectedCustomerIds
                        .filter(
                          (c) =>
                            c.customerName ===
                            formikRef.current?.values.customer // ✅ match current selected customer
                        )
                        .map((c) => ({
                          value: c.customerLocation,
                          label:
                            locations.find(
                              (loc) => loc.id === c.customerLocation
                            )?.name || `Branch ${c.customerLocation}`, // ✅ show actual name if available
                        }))}
                    />
                  </Grid>

                  {/* Resolved fields */}
                  {complaintId && (
                    <Grid item xs={12} sm={6}>
                      <FormikControl
                        control="SelectField"
                        name="isResolved"
                        label="Resolved"
                        options={[
                          { label: "Resolved", value: true },
                          { label: "Not Resolved", value: false },
                        ]}
                        error={Boolean(touched.isResolved && errors.isResolved)}
                        helperText={touched.isResolved && errors.isResolved}
                        {...getFieldProps("isResolved")}
                      />
                    </Grid>
                  )}
                  {complaintId && getFieldProps("isResolved").value && (
                    <Grid item xs={12} sm={6}>
                      <FormikControl
                        control="DatePicker"
                        name="resolvedDate"
                        label="Resolved Date"
                        dateOrTimeOnly="date"
                        error={Boolean(
                          touched.resolvedDate && errors.resolvedDate
                        )}
                        helperText={touched.resolvedDate && errors.resolvedDate}
                        {...getFieldProps("resolvedDate")}
                      />
                    </Grid>
                  )}

                  {/* Detail */}
                  <Grid item xs={12}>
                    <FormikControl
                      control="Textarea"
                      multiline
                      rows={5}
                      required
                      name="detail"
                      label="Detail"
                      error={Boolean(touched.detail && errors.detail)}
                      helperText={touched.detail && errors.detail}
                      {...getFieldProps("detail")}
                    />
                  </Grid>

                  {/* Attachments */}
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">Attachments</CTypography>
                  </Grid>

                  {/* Existing Attachments */}
                  {selectedComplaint?.attachments
                    ?.filter((a) => !attachmentToRemove.includes(a.fileUrl))
                    .map((attachment) => (
                      <Grid
                        item
                        xs={12}
                        key={attachment.fileUrl}
                        container
                        alignItems="center"
                      >
                        <Grid item xs={10}>
                          <CTypography>{attachment.fileName}</CTypography>
                        </Grid>
                        <Grid item xs={2} textAlign="right">
                          <IconButton
                            onClick={() => {
                              setSelectedAttachment(attachment);
                              dispatch(
                                setDialog({
                                  open: true,
                                  dialogName: "RemoveAttachmentConfirmation",
                                })
                              );
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}

                  {/* New File Upload */}
                  <Grid item xs={12}>
                    <FileDropzone
                      acceptedFileTypes={{
                        "image/jpg": [],
                        "image/jpeg": [],
                        "image/png": [],
                        "video/mp4": [],
                      }}
                      onSelectedFilesChange={setFiles}
                      isDisabled={false}
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>

      {/* Confirmation Modal */}
      <CModalConfirmation
        dialogName="RemoveAttachmentConfirmation"
        title="Confirmation"
        handleSaveButtonText="Yes"
        handleCancelButtonText="No"
        handleSave={() =>
          setAttachmentToRemove((prev) => [
            ...prev,
            selectedAttachment?.fileUrl || "",
          ])
        }
        handleCancel={() =>
          dispatch(
            setDialog({
              open: false,
              dialogName: "RemoveAttachmentConfirmation",
            })
          )
        }
        content={`Do you want to remove this ${selectedAttachment?.fileName} attachment?`}
      />
    </Box>
  );
};
